import type {
  AnalyticsPluginConfig,
  FocusPoint,
  KeldaIngest,
  CloudIngestLocation,
  CloudArchiveLocation,
  CentrallyRenderedSequenceInfo,
} from './itemPropTypes';
import type { SubclipParams } from './subclipDto';
import type { PersonDetectionParams, ArchiveFileTransferParams } from './asyncOperationPrimitives';
import type { ItemTranscriptionSettings } from './asyncOperationModels';
import type { AsyncOperationType } from './asyncOperationType';
import type { CompleteType } from '../util/general';
import { isObject } from '@mimir/primitive/type-guards/isObject';
import isEqual from 'lodash/isEqual';
import type { UploadProgressInfo } from '../upload/uploadProgression';
import type { ProgressInfo } from './progress';
import type { TextDetectionSettings } from './textDetectionSettings';
import type { LiveSourcePipelineId } from './liveSourceType';
import type { LiveRecording } from './liveItemProps';
import type { SearchItemType } from './itemTypes';
import { ItemType } from './itemTypes';
import type { ItemStateType } from './itemState';
import { ItemState } from './itemState';
import type { ClipListEntryDto } from './clipListDto';
import type { ExtendedItemDto } from './extendedItemDto';
import type { additionalSelectiveItemEntryKeys, standardItemEntryKeys } from './itemModelPrimitives';
import type { AnyTimelineFormat } from './cutter/timelineDto';

export const isFocusPoint = (val: unknown): val is FocusPoint => {
  if (typeof val !== 'object') {
    return false;
  }
  if (!val) {
    return false;
  }
  return 'x' in val && 'y' in val;
};

const clamp = (v: number) => {
  if (v < 0) {
    return 0;
  }
  if (v > 1) {
    return 1;
  }
  return v;
};

export const sanitizeFocusPoint = (val: FocusPoint): FocusPoint => {
  return {
    x: clamp(val.x),
    y: clamp(val.y)
  };
};

export const isInstanceOfKeldaIngest = (obj: unknown): obj is KeldaIngest => {
  if (!isObject(obj)) {
    return false;
  }
  const probablyKeldaIngest = obj as Partial<KeldaIngest>;
  return (typeof probablyKeldaIngest.requestId === 'string');
};

export const isInstanceOfSubclipParams = (obj: unknown): obj is SubclipParams => {
  const possibleSubclipParams = obj as Partial<SubclipParams>;
  return !!(
    possibleSubclipParams
    && typeof possibleSubclipParams.startTime === 'number'
    && typeof possibleSubclipParams.endTime === 'number'
  );
};

export interface PendingSubclipOperationExtraParams {
  subclipParams: SubclipParams;
  billingInfo: {
    executedBy?: string;
    subclipDuration: number;
  };
}

export interface StartAnalyticsJobRequestDto {
  itemId: string,
  forced?: boolean,
  operationType: AsyncOperationType,
  operationParams?: ItemTranscriptionSettings | PersonDetectionParams | SubclipParams | ArchiveFileTransferParams | TextDetectionSettings,
  supportTenantId?: string;
}

const completePipelineId: (p: LiveSourcePipelineId) => CompleteType<LiveSourcePipelineId> = p => ({
  liveSourceId: p.liveSourceId,
  liveSourceType: p.liveSourceType,
  pipelineId: p.pipelineId,
  s3Output: p.s3Output ?? false,
});

export function isSamePipelineId(p1: LiveSourcePipelineId, p2: LiveSourcePipelineId): boolean {
  return isEqual(completePipelineId(p1), completePipelineId(p2));
}

/**
 * One entry in the collection of items on the getItems() API
 */
export type StandardItemEntryDto = CompleteType<Pick<ExtendedItemDto, typeof standardItemEntryKeys[number]> & { thumbnail?: string }>;
export type SelectiveItemEntryDto = CompleteType<Pick<StandardItemEntryDto, 'id'> & Partial<StandardItemEntryDto & Pick<ExtendedItemDto, typeof additionalSelectiveItemEntryKeys[number]>>>;
export type GetItemsDto = StandardItemEntryDto & SelectiveItemEntryDto;
export type ItemEntryEntity = StandardItemEntryDto;

type ItemCreationResponseDtoBase = Pick<
  ExtendedItemDto,
  'id' | 'mediaType' | 'metadata' | 'itemType' | 'transcriptionEnabled' | 'folderParents' | 'createdBy' | 'visibleTo' | 'updatableBy' | 'languageCode' | 'metadataVersion' | 'customIcons'
  >;

export interface ItemCreationResponse extends ItemCreationResponseDtoBase {
  celebrityDetectionEnabled?: boolean;
  labelDetectionEnabled?: boolean;
  textDetectionEnabled?: boolean;
  personDetectionEnabled?: boolean;
  progressInfo?: ProgressInfo;
  liveRecording?: LiveRecording;
  analyticPlugins?: Record<string, AnalyticsPluginConfig>;
}

/**
 * Not all item types can be used for a GET on /items endpoint
 */
export const notGettableItemTypes: SearchItemType[] = [ItemType.FOLDER, ItemType.PERSON];


export const isInstanceOfCentrallyRenderedSequenceInfo = (obj: unknown): obj is CentrallyRenderedSequenceInfo => {
  if (!isObject(obj)) {
    return false;
  }
  return !!((obj as Partial<CentrallyRenderedSequenceInfo>).ameWatchFolderName);
};


export function isInstanceOfProgressInfo(obj: unknown): obj is ProgressInfo {
  return isObject(obj)
    && 'status' in obj && typeof obj['status'] === 'string'
    && (!('percentage' in obj) || ('percentage' in obj && typeof obj['percentage'] === 'number'))
    && (!('description' in obj) || ('description' in obj && typeof obj['description'] === 'string'))
    && (!('errors' in obj) || ('errors' in obj && Array.isArray(obj['errors'])));
}

export function isUploadProgressInfo(obj: unknown): obj is UploadProgressInfo {
  return isObject(obj)
    && isObject(obj['bytes'])
    && isObject(obj['files']);
}

export const isDeleting = (item: { itemState?: ItemStateType, isDeleting?: boolean }) =>
  item.itemState === ItemState.DELETING || item.isDeleting === true;

// Legacy cutter
export type MimirSequence = {
  id: string,
  version: number,
  clips: ClipListEntryDto[]
};

// New cutter
export type SequenceLoadRequest = {
  id: string;
  version: number;
  timeline: AnyTimelineFormat;

  // A value that can be changed to force reload of the same sequence
  requestId: string;
  unsavedTimeline?: boolean;
};

export function isInstanceOfCloudIngestLocation(obj: Partial<CloudIngestLocation>): obj is CompleteType<CloudIngestLocation> {
  if (!isObject(obj)) {
    return false;
  }
  return Boolean(obj.ingestSourceFullPath && obj.ingestSourceS3Bucket && obj.ingestSourceStorageId);
}

export function isInstanceOfCloudArchiveLocation(obj: Partial<CloudArchiveLocation>): obj is CompleteType<CloudArchiveLocation> {
  if (!isObject(obj)) {
    return false;
  }
  return Boolean(obj.archiveS3Bucket && obj.archiveLocationFullPath);
}
